"use client";

import { Instagram, Music, Beer } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        <li className="h-4 w-24 bg-muted/40 animate-pulse rounded-md" />
        <li className="h-4 w-20 bg-muted/40 animate-pulse rounded-md" />
        <li className="h-4 w-28 bg-muted/40 animate-pulse rounded-md" />
        <li className="h-4 w-16 bg-muted/40 animate-pulse rounded-md" />
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="text-foreground/85 hover:text-[var(--primary)] transition-colors duration-300 font-default text-sm tracking-wide">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
const hours = [{
  day: "MON",
  time: "6AM — 8PM"
}, {
  day: "TUE",
  time: "6AM — 8PM"
}, {
  day: "WED",
  time: "6AM — 8PM"
}, {
  day: "THU",
  time: "6AM — 8PM"
}, {
  day: "FRI",
  time: "6AM — 8PM"
}, {
  day: "SAT",
  time: "8AM — 2PM"
}, {
  day: "SUN",
  time: "CLOSED"
}];
const bottomLinks = [{
  label: "PRIVACY",
  href: "#"
}, {
  label: "TERMS",
  href: "#"
}, {
  label: "DISCLAIMER",
  href: "#"
}];
const socials = [{
  icon: Instagram,
  label: "Instagram",
  href: "https://instagram.com/coachkirafit"
}, {
  icon: Music,
  label: "Spotify",
  href: "#"
}, {
  icon: Beer,
  label: "Community",
  href: "#"
}];
export default function FooterEssentials() {
  return <section data-section-id="4763" id="footer-essentials" className="relative bg-background text-foreground overflow-hidden border-t border-[var(--primary)]/40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center select-none">
        <span className="font-default font-light leading-none tracking-tighter whitespace-nowrap text-foreground/[0.04]" style={{
        fontSize: "clamp(120px, 28vw, 320px)"
      }}>
          KIRA
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-48 lg:pt-52 pb-10">
        {}
        <div className="grid grid-cols-12 mb-20 md:mb-28">
          <blockquote className="col-span-12 md:col-start-2 md:col-span-10 lg:col-span-10">
            <p className="font-default font-bold italic leading-[1.05] tracking-tight text-foreground text-4xl sm:text-5xl md:text-6xl lg:text-[96px]">
              Train with purpose.
              <br />
              Live with strength
              <span className="text-[var(--primary)]">.</span>
            </p>
          </blockquote>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
          {}
          <div className="space-y-6">
            <Link to="/HomePage" aria-label="Coach Kira Fit — Home" className="inline-block">
              <WvcLogo className="h-10 w-auto" />
            </Link>
            <div className="font-mono text-[12px] leading-relaxed tracking-wider text-foreground/70 uppercase">
              <div>STUDIO HQ</div>
              <div>FITNESS DISTRICT</div>
              <div>47.6062°N 122.3321°W</div>
              <div className="mt-4 text-foreground/85">+1 (555) 987-6543</div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-mono font-default text-[11px] uppercase tracking-[0.2em] text-[var(--primary)]">
              Navigate
            </h3>
            <MenuProvider menu_id="7">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-mono font-default text-[11px] uppercase tracking-[0.2em] text-[var(--primary)]">
              Schedule
            </h3>
            <ul className="space-y-2.5">
              {hours.map((h, i) => <li data-index={i} key={h.day} className="flex items-baseline gap-2 font-mono text-[12px] text-foreground/80">
                  <span className="uppercase tracking-wider text-foreground">{h.day}</span>
                  <span aria-hidden="true" className="flex-1 border-b border-dotted border-foreground/25 translate-y-[-3px]" />
                  <span className="tabular-nums tracking-wide text-foreground/85">
                    {h.time}
                  </span>
                </li>)}
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-mono font-default text-[11px] uppercase tracking-[0.2em] text-[var(--primary)]">
              Contact
            </h3>
            <ul className="space-y-3 font-default text-sm">
              <li>
                <Link to="mailto:coach@coachkirafit.com" className="text-foreground/85 hover:text-[var(--primary)] transition-colors duration-300">
                  coach@coachkirafit.com
                </Link>
              </li>
              <li>
                <Link to="tel:+15559876543" className="text-foreground/85 hover:text-[var(--primary)] transition-colors duration-300 font-mono text-[13px]">
                  +1 (555) 987-6543
                </Link>
              </li>
            </ul>

            <div className="flex items-center gap-4 pt-2">
              {socials.map((s, i) => {
              const Icon = s.icon;
              return <Link data-index={i} key={s.label} to={s.href} newTab aria-label={s.label} className="group inline-flex items-center justify-center w-10 h-10 border border-[var(--primary)]/60 text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--primary-foreground)] transition-all duration-300 rounded-md">
                    <Icon className="w-4 h-4" strokeWidth={1.25} />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        {}
        <Separator className="my-12 bg-[var(--primary)]/30" />

        {}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground/65 order-2 md:order-1">Coach Kira Fit © 2026 · All Rights Reserved</p>

          {}
          <div aria-hidden="true" className="order-1 md:order-2 md:absolute md:left-1/2 md:-translate-x-1/2 flex items-center justify-center text-[var(--primary)]/70">
            <svg width="48" height="14" viewBox="0 0 48 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-80">
              <path d="M1 7C5 1 9 1 13 7C17 13 21 13 25 7C29 1 33 1 37 7C41 13 45 13 47 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
            </svg>
          </div>

          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 order-3">
            {bottomLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground/65 hover:text-[var(--primary)] transition-colors duration-300">
                  {link.label}
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}